import { useCallback, useMemo, forwardRef } from 'react';

import { Multiselect } from '../';

import styles from './FiltersPanel.module.css';
import type { FiltersPanelProps } from './FiltersPanel.types';

/**
 * FiltersPanel component for displaying filter controls in a slide-down panel
 * 
 * Features:
 * - Slide-down animation when opening/closing
 * - Row of Multiselect components for each filter
 * - Clear All button to reset all filters
 * - Close button to collapse the panel
 * - Responsive grid layout for filter dropdowns
 * - Count/badge showing number of active filters
 *
 * @example
 * ```tsx
 * const filters = [
 *   {
 *     key: 'type',
 *     label: 'Type',
 *     options: [
 *       { value: 'image', label: 'Image' },
 *       { value: 'video', label: 'Video' },
 *     ],
 *   },
 *   {
 *     key: 'size',
 *     label: 'Size',
 *     options: [
 *       { value: 'small', label: 'Small' },
 *       { value: 'large', label: 'Large' },
 *     ],
 *   },
 * ];
 *
 * const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
 *
 * <FiltersPanel
 *   isOpen={showFilters}
 *   onClose={() => setShowFilters(false)}
 *   filters={filters}
 *   values={filterValues}
 *   onChange={(key, values) => setFilterValues(prev => ({ ...prev, [key]: values }))}
 *   onClearAll={() => setFilterValues({})}
 * />
 * ```
 */
export const FiltersPanel = forwardRef<HTMLDivElement, FiltersPanelProps>(
  (
    {
      isOpen,
      onClose,
      filters,
      values,
      onChange,
      onClearAll,
      clearButtonLabel = 'Clear All',
      closeButtonLabel = 'Close',
      title = 'Filters',
      showFilterCount = true,
      className,
      testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    // Calculate total active filter count
    const activeFilterCount = useMemo(() => {
      return Object.values(values).reduce((count, filterValues) => {
        return count + (filterValues?.length || 0);
      }, 0);
    }, [values]);

    // Handle filter value change
    const handleFilterChange = useCallback(
      (filterKey: string) => (newValues: string[]) => {
        onChange(filterKey, newValues);
      },
      [onChange]
    );

    // Handle clear all click
    const handleClearAll = useCallback(() => {
      onClearAll();
    }, [onClearAll]);

    // Handle close click
    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    // Build container class names
    const containerClassNames = [styles.filtersPanel, className]
      .filter(Boolean)
      .join(' ');

    // Resolve test ID with new props taking precedence
    const resolvedTestId = dataTestId || testId;

    return (
      <div
        ref={ref}
        className={containerClassNames}
        data-open={isOpen}
        data-testid={resolvedTestId}
        data-id={dataId}
        role="region"
        aria-label={title}
        aria-hidden={!isOpen}
      >
        {/* Header with title, count, and actions */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h3 className={styles.title}>{title}</h3>
            {showFilterCount && activeFilterCount > 0 && (
              <span
                className={styles.filterCount}
                aria-label={`${activeFilterCount} active filters`}
              >
                {activeFilterCount}
              </span>
            )}
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClearAll}
              data-has-selections={activeFilterCount > 0}
              disabled={activeFilterCount === 0}
              aria-label={clearButtonLabel}
              data-testid={resolvedTestId ? `${resolvedTestId}-clear` : undefined}
            >
              {clearButtonLabel}
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label={closeButtonLabel}
              data-testid={resolvedTestId ? `${resolvedTestId}-close` : undefined}
            >
              {closeButtonLabel}
            </button>
          </div>
        </div>

        {/* Filters grid */}
        <div className={styles.filtersScrollArea}>
          <div className={styles.filtersGrid}>
            {filters.map((filter) => {
              const filterValues = values[filter.key] || [];
              
              return (
                <div
                  key={filter.key}
                  className={styles.filterItem}
                  data-testid={resolvedTestId ? `${resolvedTestId}-filter-${filter.key}` : undefined}
                >
                  <label className={styles.filterLabel}>
                    {filter.label}
                  </label>
                  <Multiselect
                    options={filter.options}
                    value={filterValues}
                    onChange={handleFilterChange(filter.key)}
                    placeholder={filter.placeholder || `Select ${filter.label.toLowerCase()}...`}
                    size="sm"
                    fullWidth
                    data-testid={resolvedTestId ? `${resolvedTestId}-multiselect-${filter.key}` : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

FiltersPanel.displayName = 'FiltersPanel';

export default FiltersPanel;